import React, { useState, useCallback } from "react";
import axios from "axios";

const STATUS_META = {
  reconciled:  { label: "Reconciled",  bg: "#d4edda", color: "#155724" },
  matched:     { label: "Matched",     bg: "#d4edda", color: "#155724" },
  variance:    { label: "Variance",    bg: "#fff3cd", color: "#856404" },
  broker_only: { label: "Broker Only", bg: "#fff3cd", color: "#856404" },
  system_only: { label: "System Only", bg: "#f8d7da", color: "#721c24" },
  api_error:   { label: "API Error",   bg: "#f8d7da", color: "#721c24" },
};

const fmtPnl = (v) => {
  if (v == null) return <span style={{ color: "#aaa" }}>—</span>;
  const color = v >= 0 ? "#28a745" : "#dc3545";
  const abs = Math.abs(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return <span style={{ color, fontWeight: 600 }}>{v >= 0 ? "+" : "−"}${abs}</span>;
};

const fmtPrice = (v) => v == null ? "—" : `$${Number(v).toFixed(2)}`;

const Badge = ({ status }) => {
  const m = STATUS_META[status] || { label: status, bg: "#e9ecef", color: "#333" };
  return (
    <span style={{ background: m.bg, color: m.color, padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" }}>
      {m.label}
    </span>
  );
};

export default function ReconcileCard({ username }) {
  const todayStr = new Date().toISOString().slice(0, 10);
  const [open, setOpen]           = useState(false);
  const [fromDate, setFromDate]   = useState(todayStr);
  const [toDate, setToDate]       = useState(todayStr);
  const [rows, setRows]           = useState(null);
  const [summary, setSummary]     = useState(null);
  const [loading, setLoading]     = useState(false);
  const [patching, setPatching]   = useState(false);
  const [patchResult, setPatchResult] = useState(null);
  const [error, setError]         = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");

  const load = useCallback(async () => {
    if (!username) return;
    setLoading(true); setError(null); setPatchResult(null);
    try {
      const r = await axios.get("/pnl/reconciliation", {
        params: { username, from_date: fromDate, to_date: toDate },
      });
      setRows(r.data.rows);
      setSummary(r.data.summary);
    } catch {
      setError("Failed to load reconciliation data.");
    } finally {
      setLoading(false);
    }
  }, [username, fromDate, toDate]);

  const patch = useCallback(async () => {
    if (!username) return;
    setPatching(true); setError(null);
    try {
      const r = await axios.post("/pnl/reconciliation/patch", { username });
      setPatchResult(r.data.patched);
      const r2 = await axios.get("/pnl/reconciliation", {
        params: { username, from_date: fromDate, to_date: toDate },
      });
      setRows(r2.data.rows); setSummary(r2.data.summary);
    } catch (e) {
      setError(e.response?.data?.error || "Patch failed.");
    } finally {
      setPatching(false);
    }
  }, [username, fromDate, toDate]);

  const handleToggle = () => {
    const next = !open;
    setOpen(next);
    if (next && rows === null && !loading) load();
  };

  const filtered = rows
    ? (statusFilter === "all" ? rows : rows.filter(r => r.status === statusFilter))
    : [];

  const hasIssues = rows && rows.some(r => ["variance", "broker_only", "system_only"].includes(r.status));
  const statusCounts = summary?.status_counts || {};

  return (
    <div style={{ marginTop: 28 }}>
      {/* ── Collapsible header ── */}
      <button onClick={handleToggle} style={{
        width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
        background: "#fff", border: "1px solid #e0e0e0", borderRadius: open ? "10px 10px 0 0" : 10,
        padding: "13px 18px", cursor: "pointer", boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: "#2c3e50" }}>Broker Reconciliation</span>
          {rows !== null && !loading && (
            hasIssues
              ? <span style={{ fontSize: 12, fontWeight: 700, padding: "2px 10px", borderRadius: 12, background: "#fff3cd", color: "#856404" }}>
                  Action needed
                </span>
              : <span style={{ fontSize: 12, fontWeight: 700, padding: "2px 10px", borderRadius: 12, background: "#d4edda", color: "#155724" }}>
                  ✓ Reconciled
                </span>
          )}
        </div>
        <span style={{ fontSize: 18, color: "#999", transform: open ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>&#8964;</span>
      </button>

      {open && (
        <div style={{ background: "#fff", border: "1px solid #e0e0e0", borderTop: "none", borderRadius: "0 0 10px 10px", padding: "18px 18px 24px", boxShadow: "0 4px 12px rgba(0,0,0,0.06)" }}>

          {/* ── Toolbar ── */}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 11, color: "#888", marginBottom: 3 }}>From</div>
              <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
                style={{ padding: "6px 10px", border: "1px solid #ddd", borderRadius: 6, fontSize: 13 }} />
            </div>
            <div>
              <div style={{ fontSize: 11, color: "#888", marginBottom: 3 }}>To</div>
              <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
                style={{ padding: "6px 10px", border: "1px solid #ddd", borderRadius: 6, fontSize: 13 }} />
            </div>
            <button onClick={load} disabled={loading}
              style={{ padding: "7px 16px", background: "#2c3e50", color: "#fff", border: "none", borderRadius: 6, fontWeight: 600, cursor: "pointer", fontSize: 13, alignSelf: "flex-end" }}>
              {loading ? "Loading…" : "↺ Load"}
            </button>
            {rows !== null && hasIssues && (
              <button onClick={patch} disabled={patching}
                style={{ padding: "7px 16px", background: "#fd7e14", color: "#fff", border: "none", borderRadius: 6, fontWeight: 600, cursor: "pointer", fontSize: 13, alignSelf: "flex-end" }}>
                {patching ? "Patching…" : "Reconcile Now"}
              </button>
            )}
          </div>

          {error && <div style={{ color: "#dc3545", marginBottom: 10, fontSize: 13 }}>{error}</div>}

          {patchResult && (
            <div style={{ background: "#d4edda", color: "#155724", padding: "8px 14px", borderRadius: 6, marginBottom: 14, fontSize: 13 }}>
              Patched: {patchResult.inserted} inserted · {patchResult.updated} updated · {patchResult.deleted} deleted
            </div>
          )}

          {/* ── Summary tiles ── */}
          {summary && (
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
              {[
                { label: "Broker P&L",   val: summary.total_broker_pnl },
                { label: "Our P&L",      val: summary.total_our_pnl },
                { label: "Accepted P&L", val: summary.total_accepted_pnl },
              ].map(t => (
                <div key={t.label} style={{ background: "#f8f9fb", borderRadius: 8, padding: "10px 16px", minWidth: 140 }}>
                  <div style={{ fontSize: 11, color: "#888", marginBottom: 3 }}>{t.label}</div>
                  <div style={{ fontSize: 16, fontWeight: 700 }}>{fmtPnl(t.val)}</div>
                </div>
              ))}
              {Object.entries(statusCounts).map(([st, cnt]) => {
                const m = STATUS_META[st] || { label: st, bg: "#e9ecef", color: "#333" };
                return (
                  <div key={st} onClick={() => setStatusFilter(statusFilter === st ? "all" : st)}
                    style={{ background: m.bg, borderRadius: 8, padding: "10px 16px", minWidth: 100, cursor: "pointer" }}>
                    <div style={{ fontSize: 11, color: m.color, fontWeight: 700 }}>{m.label}</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: m.color }}>{cnt}</div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── Status filter chips ── */}
          {rows !== null && (
            <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
              {["all", ...Object.keys(statusCounts)].map(st => (
                <button key={st} onClick={() => setStatusFilter(st)} style={{
                  padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600,
                  border: "1px solid #ddd", cursor: "pointer",
                  background: statusFilter === st ? "#2c3e50" : "#fff",
                  color: statusFilter === st ? "#fff" : "#555",
                }}>
                  {st === "all" ? `All (${rows.length})` : `${STATUS_META[st]?.label || st} (${statusCounts[st]})`}
                </button>
              ))}
            </div>
          )}

          {/* ── Table ── */}
          {filtered.length > 0 && (
            <div style={{ overflowX: "auto", borderRadius: 8, border: "1px solid #f0f0f0" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ background: "#f8f9fa", borderBottom: "2px solid #dee2e6" }}>
                    {["Date","Symbol","CE/PE","Strike","Expiry","Lots","Sold At","Bought At","Broker P&L","Our P&L","Variance","Accepted P&L","Status"].map(h => (
                      <th key={h} style={{ padding: "8px 10px", textAlign: "left", fontWeight: 700, color: "#555", fontSize: 11, whiteSpace: "nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r, i) => (
                    <tr key={i} style={{ borderBottom: "1px solid #f5f5f5", background: i % 2 === 0 ? "#fff" : "#fcfcfc" }}>
                      <td style={td}>{r.exit_date}</td>
                      <td style={{ ...td, fontWeight: 700 }}>{r.symbol}</td>
                      <td style={{ ...td, color: r.optiontype === "CE" ? "#0d6efd" : "#e83e8c", fontWeight: 700 }}>{r.optiontype}</td>
                      <td style={td}>${(r.strike || 0).toLocaleString("en-US")}</td>
                      <td style={td}>{(r.expiry || "").slice(0, 10)}</td>
                      <td style={{ ...td, textAlign: "right" }}>{r.lots ?? "—"}</td>
                      <td style={{ ...td, textAlign: "right" }}>{fmtPrice(r.sold_at)}</td>
                      <td style={{ ...td, textAlign: "right" }}>{fmtPrice(r.bought_at)}</td>
                      <td style={{ ...td, textAlign: "right" }}>{fmtPnl(r.broker_pnl)}</td>
                      <td style={{ ...td, textAlign: "right" }}>{fmtPnl(r.our_pnl)}</td>
                      <td style={{ ...td, textAlign: "right" }}>
                        {r.variance == null ? <span style={{ color: "#aaa" }}>—</span>
                          : r.variance === 0 ? <span style={{ color: "#28a745" }}>$0</span>
                          : fmtPnl(r.variance)}
                      </td>
                      <td style={{ ...td, textAlign: "right" }}>{fmtPnl(r.accepted_pnl)}</td>
                      <td style={td}><Badge status={r.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {rows !== null && filtered.length === 0 && (
            <div style={{ color: "#aaa", padding: "20px 0", textAlign: "center", fontSize: 13 }}>
              No entries for selected filter.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const td = { padding: "7px 10px", whiteSpace: "nowrap" };
